import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { BsFiletypeXls } from "react-icons/bs";
import { FiArrowLeft } from "react-icons/fi";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { getActionTypeLabel } from "../../OtherData/actionTypeMap";
import { errorAlert } from "../../Components/Alerts";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import {
    accountDetailColumns,
    accountDetailMappings,
    accountMappings,
    accountsColumns,
} from "../../Components/ExcelConverter/AccountingReportsHeader";
import { currencyFormatter } from "../../Components/CurrencyFormat";
import "./AccountsReportPage.scss";
import "../../Style/__button.scss";

const LEDGER_ID = 105;

const formatCurrency = (value) => currencyFormatter(Number(value || 0));

function formatDateForDisplay(date) {
    if (!date) {
        return "";
    }

    if (date instanceof Date) {
        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    const raw = String(date).trim();
    if (!raw) {
        return "";
    }

    const dottedMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dottedMatch) {
        return raw;
    }

    const slashMatch = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashMatch) {
        const [, year, month, day] = slashMatch;
        return `${day}.${month}.${year}`;
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}.${month}.${year}`;
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
        return raw;
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}.${month}.${year}`;
}

function normalizeQuickFilter(value) {
    if (Array.isArray(value) && value.length > 0) {
        return value.join(" ").trim();
    }

    return "";
}

function parseDateValue(value) {
    const raw = String(value || "").trim();
    if (!raw) {
        return null;
    }

    const dottedMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dottedMatch) {
        const [, day, month, year] = dottedMatch;
        return new Date(`${year}-${month}-${day}T00:00:00`);
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        return new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T00:00:00`);
    }

    const parsedDate = new Date(raw);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function applyQuickSearch(rows, quickFilter, fields) {
    const searchText = normalizeQuickFilter(quickFilter).toLowerCase();
    if (!searchText) {
        return rows;
    }

    return rows.filter((row) => fields.some((field) => String(row[field] ?? "").toLowerCase().includes(searchText)));
}

function compareValues(left, right, field) {
    const numericFields = new Set(["rowNumber", "debit", "credit", "balance", "progressive"]);
    const dateFields = new Set(["date"]);

    if (dateFields.has(field)) {
        const leftDate = parseDateValue(left);
        const rightDate = parseDateValue(right);
        return (leftDate?.getTime() || 0) - (rightDate?.getTime() || 0);
    }

    if (numericFields.has(field)) {
        return Number(left || 0) - Number(right || 0);
    }

    return String(left ?? "").localeCompare(String(right ?? ""), "sq", { sensitivity: "base" });
}

function applyColumnFilters(rows, filterModel, quickFilter, fields) {
    const filteredItems = filterModel?.items || [];
    const afterQuickSearch = applyQuickSearch(rows, quickFilter, fields);

    if (filteredItems.length === 0) {
        return afterQuickSearch;
    }

    return afterQuickSearch.filter((row) => filteredItems.every((filterItem) => {
        if (!filterItem?.field) {
            return true;
        }

        const value = row[filterItem.field];
        const filterValue = String(filterItem.value ?? "").trim();
        if (!filterValue) {
            return true;
        }

        const operator = filterItem.operator || "contains";
        const normalizedValue = String(value ?? "");

        if (filterItem.field === "date") {
            const rowDate = parseDateValue(value);
            const filterDate = parseDateValue(filterValue);
            if (!rowDate || !filterDate) {
                return false;
            }

            const rowTime = rowDate.getTime();
            const filterTime = filterDate.getTime();

            switch (operator) {
                case "gte":
                    return rowTime >= filterTime;
                case "gt":
                    return rowTime > filterTime;
                case "lte":
                    return rowTime <= filterTime;
                case "lt":
                    return rowTime < filterTime;
                case "equals":
                    return rowTime === filterTime;
                default:
                    return normalizedValue.toLowerCase().includes(filterValue.toLowerCase());
            }
        }

        const numericFields = new Set(["rowNumber", "debit", "credit", "balance", "progressive"]);
        if (numericFields.has(filterItem.field)) {
            const rowNumber = Number(value || 0);
            const filterNumber = Number(filterValue.replace(/,/g, "."));
            if (Number.isNaN(filterNumber)) {
                return false;
            }

            switch (operator) {
                case "gte":
                    return rowNumber >= filterNumber;
                case "gt":
                    return rowNumber > filterNumber;
                case "lte":
                    return rowNumber <= filterNumber;
                case "lt":
                    return rowNumber < filterNumber;
                case "equals":
                    return rowNumber === filterNumber;
                default:
                    return normalizedValue.includes(filterValue);
            }
        }

        switch (operator) {
            case "equals":
                return normalizedValue.toLowerCase() === filterValue.toLowerCase();
            case "contains":
            default:
                return normalizedValue.toLowerCase().includes(filterValue.toLowerCase());
        }
    }));
}

function sortRows(rows, sortModel) {
    const sortItem = sortModel?.[0];
    if (!sortItem?.field || !sortItem?.sort) {
        return rows;
    }

    const sortedRows = [...rows];
    sortedRows.sort((leftRow, rightRow) => {
        const comparison = compareValues(leftRow[sortItem.field], rightRow[sortItem.field], sortItem.field);
        return sortItem.sort === "desc" ? -comparison : comparison;
    });

    return sortedRows;
}

function paginateRows(rows, paginationModel) {
    const currentPage = paginationModel?.page || 0;
    const currentPageSize = paginationModel?.pageSize || 10;
    const startIndex = currentPage * currentPageSize;
    return rows.slice(startIndex, startIndex + currentPageSize);
}

export default function AccountsReceivablePage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [quickFilter, setQuickFilter] = useState([]);
    const [companiesData, setCompaniesData] = useState([]);
    const [logoPath, setLogoPath] = useState("");

    const [showDetails, setShowDetails] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [accountDetailName, setAccountDetailName] = useState("");
    const [detailsRequestVersion, setDetailsRequestVersion] = useState(0);
    const [accountDetails, setAccountDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [detailsSortModel, setDetailsSortModel] = useState([]);
    const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
    const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);
    const [detailsDateSortDirection, setDetailsDateSortDirection] = useState("desc");
    const [detailsRowCount, setDetailsRowCount] = useState(0);
    const [detailsTotals, setDetailsTotals] = useState({ debit: 0, credit: 0, progressive: 0 });
    const detailsFetchTokenRef = useRef(0);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "Reports/accounts", {
                headers,
                params: { ledger: LEDGER_ID, fiscalYear },
            });

            setData(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            const companyItems = Array.isArray(response?.data) ? response.data : [];
            setCompaniesData(companyItems);
            setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const fetchDetailsData = async () => {
        if (!showDetails || !selectedAccountId) {
            return;
        }

        const requestToken = detailsFetchTokenRef.current + 1;
        detailsFetchTokenRef.current = requestToken;
        setDetailsLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "Reports/AccountsDetailsPaged", {
                headers,
                params: {
                    accountID: selectedAccountId,
                    fiscalYear,
                    page: detailsPaginationModel.page,
                    pageSize: detailsPaginationModel.pageSize,
                    sort: JSON.stringify(detailsSortModel || []),
                    filter: JSON.stringify(detailsFilterModel?.items || []),
                    quickFilter: normalizeQuickFilter(detailsQuickFilter),
                },
            });

            const payload = response?.data;
            const items = Array.isArray(payload?.items)
                ? payload.items
                : (Array.isArray(payload) ? payload : []);
            const totalDebit = Number(payload?.totals?.totalDebit || 0);
            const totalCredit = Number(payload?.totals?.totalCredit || 0);

            if (requestToken !== detailsFetchTokenRef.current) {
                return;
            }

            setAccountDetails(items);
            setDetailsRowCount(Number(payload?.total || items.length || 0));
            setDetailsTotals({
                debit: totalDebit,
                credit: totalCredit,
                progressive: totalDebit - totalCredit,
            });
        } catch (error) {
            if (requestToken !== detailsFetchTokenRef.current) {
                return;
            }
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            if (requestToken === detailsFetchTokenRef.current) {
                setDetailsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [fiscalYear]);

    useEffect(() => {
        fetchCompaniesData();
    }, [fiscalYear]);

    useEffect(() => {
        fetchDetailsData();
    }, [
        showDetails,
        selectedAccountId,
        fiscalYear,
        detailsRequestVersion,
        detailsPaginationModel,
        detailsSortModel,
        detailsFilterModel,
        detailsQuickFilter,
    ]);

    const mainRows = useMemo(() => (
        data.map((item, index) => ({
            rowNumber: index + 1,
            accName: item.AccountName || "",
            address: item.Address || "",
            idNumber: item.IdentificationNumber || "",
            debit: Number(item.Debit || 0),
            credit: Number(item.Credit || 0),
            balance: Number(item.Balance || 0),
            AccountID: item.AccountID,
            AccountName: item.AccountName || "",
            rowInfo: item,
        }))
    ), [data]);

    const filteredMainRows = useMemo(() => {
        const filtered = applyColumnFilters(mainRows, filterModel, quickFilter, ["accName", "address", "idNumber"]);
        return sortRows(filtered, sortModel);
    }, [mainRows, filterModel, quickFilter, sortModel]);

    const pagedMainRows = useMemo(() => paginateRows(filteredMainRows, paginationModel), [filteredMainRows, paginationModel]);

    const mainTotals = useMemo(() => filteredMainRows.reduce((accumulator, item) => {
        accumulator.totalDebit += Number(item.debit || 0);
        accumulator.totalCredit += Number(item.credit || 0);
        accumulator.totalBalance += Number(item.balance || 0);
        return accumulator;
    }, { totalDebit: 0, totalCredit: 0, totalBalance: 0 }), [filteredMainRows]);

    const summaryFooter = useMemo(() => ({
        labelField: "idNumber",
        label: "Totali",
        values: {
            debit: formatCurrency(mainTotals.totalDebit),
            credit: formatCurrency(mainTotals.totalCredit),
            balance: formatCurrency(mainTotals.totalBalance),
        },
    }), [mainTotals]);

    const accountDetailsRows = useMemo(() => (
        accountDetails.map((item, index) => ({
            rowNumber: index + 1,
            journalId: item.JournalID || item.JournalId || 0,
            type: getActionTypeLabel(item.ActionType || ""),
            name: item.AccountName || "",
            idNumber: item.IdentificationNumber || "",
            comment: item.Comment || "",
            date: formatDateForDisplay(item.Date),
            debit: Number(item.Debit || 0),
            credit: Number(item.Credit || 0),
            progressive: Number(item.Progressive || 0),
            JournalID: item.JournalID,
            rowInfo: item,
        }))
    ), [accountDetails]);

    const mapDetailsRows = (items) => (
        (Array.isArray(items) ? items : []).map((item, index) => ({
            rowNumber: index + 1,
            journalId: item.JournalID || item.JournalId || 0,
            type: getActionTypeLabel(item.ActionType || ""),
            name: item.AccountName || "",
            idNumber: item.IdentificationNumber || "",
            comment: item.Comment || "",
            date: formatDateForDisplay(item.Date),
            debit: Number(item.Debit || 0),
            credit: Number(item.Credit || 0),
            progressive: Number(item.Progressive || 0),
            rowInfo: item,
        }))
    );

    const detailsSummaryFooter = useMemo(() => ({
        labelField: "date",
        label: "Totali",
        values: {
            debit: formatCurrency(detailsTotals.debit),
            credit: formatCurrency(detailsTotals.credit),
            progressive: formatCurrency(detailsTotals.progressive),
        },
    }), [detailsTotals]);

    const toggleDetailsDateSort = () => {
        setDetailsDateSortDirection((prevDirection) => {
            const nextDirection = prevDirection === "desc" ? "asc" : "desc";
            setDetailsSortModel([
                { field: "date", sort: nextDirection },
                { field: "journalId", sort: nextDirection },
            ]);
            setDetailsPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
            return nextDirection;
        });
    };

    const handleViewDetails = async (rowPayload) => {
        const accountId = rowPayload?.AccountID || rowPayload?.rowInfo?.AccountID;
        if (!accountId) {
            return;
        }

        setSelectedAccountId(accountId);
        setAccountDetailName(rowPayload?.AccountName || rowPayload?.rowInfo?.AccountName || "");
        setShowDetails(true);
        setDetailsPaginationModel({ page: 0, pageSize: 100 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setDetailsDateSortDirection("desc");
        setDetailsRowCount(0);
        setDetailsTotals({ debit: 0, credit: 0, progressive: 0 });
        setDetailsRequestVersion((currentValue) => currentValue + 1);
    };

    const handleBackToList = () => {
        detailsFetchTokenRef.current += 1;
        setShowDetails(false);
        setSelectedAccountId(null);
        setAccountDetails([]);
        setAccountDetailName("");
        setDetailsPaginationModel({ page: 0, pageSize: 100 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setDetailsDateSortDirection("desc");
        setDetailsRowCount(0);
        setDetailsTotals({ debit: 0, credit: 0, progressive: 0 });
    };

    const mapAccountsExportData = (items) => (
        items.map((item, index) => ({
            Sequence_no: index + 1,
            AccountName: item.accName || "",
            Address: item.address || "",
            IdentificationNumber: item.idNumber || "",
            Debit: Number(item.debit || 0),
            Credit: Number(item.credit || 0),
            Balance: Number(item.balance || 0),
        }))
    );

    const mapAccountDetailsExportData = (items) => (
        items.map((item) => ({
            ActionType: item.type || "",
            AccountName: item.name || "",
            IdentificationNumber: item.idNumber || "",
            Comment: item.comment || "",
            Date: item.date || "",
            Debit: Number(item.debit || 0),
            Credit: Number(item.credit || 0),
            Progressive: Number(item.progressive || 0),
        }))
    );

    const exportDatasetToExcel = (items, columns, mappings, fileName) => {
        if (!items || items.length === 0) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return;
        }

        const filteredData = items.map((item) => (
            columns.reduce((accumulator, column) => {
                accumulator[mappings[column] || column] = item[column];
                return accumulator;
            }, {})
        ));

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(excelFile, `${fileName}.xlsx`);
    };

    const reportPeriodLabel = useMemo(() => `${fiscalYear}`, [fiscalYear]);

    const sanitizeFileName = (value, fallback) => {
        const cleaned = String(value || "")
            .trim()
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || fallback;
    };

    const addLogo = (doc) => {
        if (!logoPath) {
            return;
        }

        doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
    };

    const addReportHeader = (doc, title, subtitle, periodLabel, rightMetaLabel = "") => {
        const company = companiesData[0];
        const pageWidth = doc.internal.pageSize.getWidth();

        addLogo(doc);

        doc.setDrawColor(40, 73, 69);
        doc.setLineWidth(0.5);
        doc.line(12, 38, pageWidth - 12, 38);

        doc.setTextColor(31, 58, 54);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, 40, 18);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(95, 127, 115);
        doc.text(subtitle, 40, 24);

        doc.setFontSize(9);
        doc.setTextColor(31, 58, 54);
        doc.text(`Periudha: ${periodLabel}`, 12, 46);
        if (rightMetaLabel) {
            doc.setFont("helvetica", "bold");
            doc.text(rightMetaLabel, pageWidth - 12, 46, { align: "right" });
        }

        if (!company) {
            return 52;
        }

        const companyDetails = [
            company.CompanyName,
            `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
            `Numri i TVSH-së: ${company.VATNumber || "-"}`,
            `Adresa: ${company.Address || "-"}`,
            `Tel: ${company.PhoneNumbers || "-"}`,
            `Email: ${company.Email || "-"}`,
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 58, 54);
        doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(95, 127, 115);
        doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

        return 52;
    };

    const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brandingLabel = "© www.accountree.net";
        const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setDrawColor(212, 226, 219);
            doc.setLineWidth(0.3);
            doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(95, 127, 115);
            doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
            doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
            doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
        }
    };

    const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
        const horizontalMargin = 12;
        const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
        const baseTableWidth = Object.values(baseColumnStyles).reduce(
            (sum, style) => sum + Number(style.cellWidth || 0),
            0,
        );
        const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
        const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
            accumulator[key] = {
                ...style,
                cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
            };
            return accumulator;
        }, {});

        doc.autoTable({
            head: [headersRow],
            body: bodyRows,
            foot: [footRow],
            startY,
            showHead: "firstPage",
            showFoot: "lastPage",
            margin: {
                top: 16,
                left: horizontalMargin,
                right: horizontalMargin,
                bottom: 16,
            },
            theme: "grid",
            styles: {
                fontSize: 7.5,
                cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
                lineColor: [212, 226, 219],
                lineWidth: 0.1,
                textColor: [31, 58, 54],
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "middle",
            },
            footStyles: {
                fillColor: [231, 240, 235],
                textColor: [31, 58, 54],
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [247, 250, 248],
            },
            didParseCell: (hookData) => {
                if (hookData.section !== "foot") {
                    return;
                }

                const alignRightFromIndex = headersRow.length >= 9 ? 6 : 4;
                if (hookData.column.index >= alignRightFromIndex) {
                    hookData.cell.styles.halign = "right";
                }
            },
            columnStyles,
        });
    };

    const buildAccountsPdfTableData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;
        let totalBalance = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.debit || 0);
            totalCredit += Number(item.credit || 0);
            totalBalance += Number(item.balance || 0);

            return [
                String(index + 1),
                item.accName || "",
                item.address || "",
                item.idNumber || "",
                formatCurrency(item.debit),
                formatCurrency(item.credit),
                formatCurrency(item.balance),
            ];
        });

        const footRow = [
            "",
            "",
            "Totali",
            "",
            formatCurrency(totalDebit),
            formatCurrency(totalCredit),
            formatCurrency(totalBalance),
        ];

        return { bodyRows, footRow };
    };

    const buildAccountDetailsPdfTableData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.debit || 0);
            totalCredit += Number(item.credit || 0);

            return [
                String(index + 1),
                item.type || "",
                item.name || "",
                item.idNumber || "",
                item.comment || "",
                item.date || "",
                formatCurrency(item.debit),
                formatCurrency(item.credit),
                formatCurrency(item.progressive),
            ];
        });

        const totalProgressive = totalDebit - totalCredit;

        const footRow = [
            "",
            "",
            "",
            "",
            "Totali",
            "",
            formatCurrency(totalDebit),
            formatCurrency(totalCredit),
            formatCurrency(totalProgressive),
        ];

        return { bodyRows, footRow };
    };

    const buildAccountsPdfDocument = async () => {
        if (!filteredMainRows.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const startY = addReportHeader(doc, "Llogaritë e Arketueshme", "Raport financiar i llogarive", reportPeriodLabel);
        const { bodyRows, footRow } = buildAccountsPdfTableData(filteredMainRows);

        addPdfTable(
            doc,
            ["#", "Emri Llogarisë", "Adresa", "Numri Identifikues", "Debi", "Kredi", "Bilanci"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 38 },
                2: { cellWidth: 34 },
                3: { cellWidth: 24 },
                4: { cellWidth: 20, halign: "right" },
                5: { cellWidth: 20, halign: "right" },
                6: { cellWidth: 20, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
        };
    };

    const exportAccountsPdf = async () => {
        const generatedDocument = await buildAccountsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, companyNameForFile } = generatedDocument;
        doc.save(`Llogarite_e_Arketueshme_${fiscalYear}_${companyNameForFile}.pdf`);
    };

    const fetchAllDetailsExportRows = async () => {
        if (!selectedAccountId) {
            return [];
        }

        const exportPageSize = Math.max(Number(detailsRowCount) || 0, 1);
        const detailsResponse = await axios.get(ApiLink.API_URL + "Reports/AccountsDetailsPaged", {
            headers,
            params: {
                accountID: selectedAccountId,
                fiscalYear,
                page: 0,
                pageSize: exportPageSize,
                sort: JSON.stringify(detailsSortModel || []),
                filter: JSON.stringify(detailsFilterModel?.items || []),
                quickFilter: normalizeQuickFilter(detailsQuickFilter),
            },
        });

        const detailsPayload = detailsResponse?.data;
        const detailsItems = Array.isArray(detailsPayload?.items)
            ? detailsPayload.items
            : (Array.isArray(detailsPayload) ? detailsPayload : []);

        return mapDetailsRows(detailsItems);
    };

    const buildAccountDetailsPdfDocument = async () => {
        const exportRows = await fetchAllDetailsExportRows();

        if (!exportRows.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const accountHeaderLabel = accountDetailName ? `Llogaria: ${accountDetailName}` : "";
        const startY = addReportHeader(
            doc,
            "Llogaria e Klientit në Detale",
            "Raport i lëvizjeve të llogarisë",
            reportPeriodLabel,
            accountHeaderLabel,
        );
        const { bodyRows, footRow } = buildAccountDetailsPdfTableData(exportRows);

        addPdfTable(
            doc,
            ["#", "Tipi Veprimit", "Emri Llogarisë", "Numri Identifikues", "Komenti", "Data", "Debi", "Kredi", "Progresive"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 18 },
                2: { cellWidth: 34 },
                3: { cellWidth: 24 },
                4: { cellWidth: 34 },
                5: { cellWidth: 18, halign: "center" },
                6: { cellWidth: 18, halign: "right" },
                7: { cellWidth: 18, halign: "right" },
                8: { cellWidth: 18, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            accountNameForFile: sanitizeFileName(accountDetailName, "Llogaria_Detaje"),
        };
    };

    const exportAccountDetailsPdf = async () => {
        const generatedDocument = await buildAccountDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, accountNameForFile } = generatedDocument;
        doc.save(`${accountNameForFile}.pdf`);
    };

    const printPdfDocument = (doc) => {
        const pdfBlob = doc.output("blob");
        const printUrl = URL.createObjectURL(pdfBlob);
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-10000px";
        printFrame.style.top = "0";
        printFrame.style.width = "1024px";
        printFrame.style.height = "768px";
        printFrame.style.border = "0";
        printFrame.style.opacity = "0";
        printFrame.style.pointerEvents = "none";
        let didPrint = false;
        let cleanupDone = false;

        const cleanup = () => {
            if (cleanupDone) {
                return;
            }

            cleanupDone = true;
            URL.revokeObjectURL(printUrl);
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.onload = () => {
            if (didPrint) {
                return;
            }

            didPrint = true;

            try {
                const frameWindow = printFrame.contentWindow;
                if (!frameWindow) {
                    cleanup();
                    errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
                    return;
                }

                window.setTimeout(() => {
                    frameWindow.focus();
                    frameWindow.onafterprint = cleanup;
                    frameWindow.print();
                    window.setTimeout(cleanup, 5000);
                }, 450);
            } catch (error) {
                cleanup();
                errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
            }
        };

        document.body.appendChild(printFrame);
        printFrame.src = printUrl;

        window.setTimeout(() => {
            cleanup();
        }, 60000);
    };

    const handlePrintAccountsPdf = async () => {
        const generatedDocument = await buildAccountsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handlePrintAccountDetailsPdf = async () => {
        const generatedDocument = await buildAccountDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handleExportMainXlsx = async () => {
        exportDatasetToExcel(
            mapAccountsExportData(filteredMainRows),
            accountsColumns,
            accountMappings,
            `Llogarite_e_Arketueshme_${fiscalYear}`,
        );
    };

    const handleExportDetailsXlsx = async () => {
        const exportRows = await fetchAllDetailsExportRows();
        exportDatasetToExcel(
            mapAccountDetailsExportData(exportRows),
            accountDetailColumns,
            accountDetailMappings,
            `${accountDetailName || "Llogaria_Detaje"}_Detaje`,
        );
    };

    const accountsColumnsGrid = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "accName", headerName: "Emri Llogarisë", minWidth: 260, width: 300 },
        { field: "address", headerName: "Adresa", minWidth: 240, width: 280 },
        { field: "idNumber", headerName: "Numri Identifikues", minWidth: 160, width: 180 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "balance", headerName: "Bilanci", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    const accountDetailsColumnsGrid = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "type", headerName: "Tipi Veprimit", minWidth: 150, width: 170 },
        { field: "name", headerName: "Emri Llogarisë", minWidth: 220, width: 260 },
        { field: "idNumber", headerName: "Numri Identifikues", minWidth: 150, width: 180 },
        { field: "comment", headerName: "Komenti", minWidth: 220, width: 260 },
        { field: "date", headerName: "Data", minWidth: 120, width: 140 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "progressive", headerName: "Progresive", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    return (
        <div className="Crud__Container ARP__page">
            <div className="ARP__content">
                {!showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FaFileInvoiceDollar}
                            title="Llogaritë e Arketueshme"
                            description="Forma e llogarive të arketueshme është e përshtatur për kontroll, filtrim dhe analizë profesionale të saldove sipas llogarive."
                            className="ARP__header"
                        />

                        <div className="ARP__reportGrid">
                            <ReusableAccountBooksGrid
                                columns={accountsColumnsGrid}
                                rows={pagedMainRows}
                                loading={loading}
                                rowCount={filteredMainRows.length}
                                height={650}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                quickFilter={quickFilter}
                                setQuickFilter={setQuickFilter}
                                showViewAction={true}
                                onView={handleViewDetails}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={summaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintAccountsPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportAccountsPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportMainXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}

                {showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FaFileInvoiceDollar}
                            title={`Llogaria e Klientit në Detale${accountDetailName ? ` - ${accountDetailName}` : ""}`}
                            description="Shfaqen të gjitha lëvizjet për llogarinë e zgjedhur."
                            className="ARP__header"
                            action={(
                                <AddActionsButton
                                    text="Kthehu te lista"
                                    onClick={handleBackToList}
                                    icon={<FiArrowLeft />}
                                    className="ARP__backBtn"
                                />
                            )}
                        />

                        <div className="ARP__reportGrid ARP__reportGrid--details">
                            <ReusableAccountBooksGrid
                                columns={accountDetailsColumnsGrid}
                                rows={accountDetailsRows}
                                loading={detailsLoading}
                                rowCount={detailsRowCount}
                                height={650}
                                paginationModel={detailsPaginationModel}
                                setPaginationModel={setDetailsPaginationModel}
                                sortModel={detailsSortModel}
                                setSortModel={setDetailsSortModel}
                                filterModel={detailsFilterModel}
                                setFilterModel={setDetailsFilterModel}
                                quickFilter={detailsQuickFilter}
                                setQuickFilter={setDetailsQuickFilter}
                                showViewAction={false}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={detailsSummaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintAccountDetailsPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportAccountDetailsPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportDetailsXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                        <button
                                            type="button"
                                            className="ARP__detailsSortToggle"
                                            onClick={toggleDetailsDateSort}
                                            title="Ndërro renditjen e datës"
                                        >
                                            Sortimi: Data {detailsDateSortDirection === "desc" ? "↓ DESC" : "↑ ASC"}
                                        </button>
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}